import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { HOME2_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the "Home 2" home-screen redesign arm — `symphonyHome2Web`.
 *
 * Variant (`true`): the home renders the new Home 2 surface. Control (`false`): today's catch-up home.
 *
 * It FAILS CLOSED to CONTROL — the shipped home — exactly like the sibling gates: a gate that has not
 * answered (or errored) shows what already works rather than flashing an unfinished surface at everyone.
 *
 * A dev-tools **Feature toggles** override wins over the conducted value (`toggleOverride` is consulted
 * first), and that override is inert without a dev-tools grant, so a real user always gets the conducted
 * value — while Home 2 is being built, turn it on from Dev Tools ▸ Feature toggles. The phones will
 * conduct their own `symphonyHome2Mobile` for the same change.
 *
 * The conducted value comes from `pages/api/flags/home2.ts` (Petri). No experiment is ramped yet, so it
 * conducts control for everyone and Home 2 shows only via the dev-tools override — the intended dev flow.
 * The route has no databag PERCENTAGE ramp leg yet (see its note); that lands when Home 2 actually ramps.
 */

/** Matches the sibling gates' bound — a gate that has not answered in time IS control. */
const GATE_TIMEOUT_MS = 1500;

let cached: Promise<boolean> | null = null;

export function home2Enabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session from the dev panel.
  const forced = toggleOverride(HOME2_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/home2`, {
        credentials: 'include',
        signal: AbortSignal.timeout(GATE_TIMEOUT_MS),
      });
      if (!res.ok) return false;
      return ((await res.json()) as boolean) === true;
    } catch {
      // Includes the abort: a gate that has not answered in time IS control.
      return false;
    }
  })();
  return cached;
}

/**
 * Resolve the arm for rendering. Starts false — the CONTROL home — so a user not in the variant never
 * sees the Home 2 surface flash in and out.
 */
export function useHome2Enabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void home2Enabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
